/**
 * Type definitions for the Azure DevOps management area.
 *
 * These mirror the API service contracts for the `devops-connections`,
 * `devops-projects`, `devops-repos`, `devops-pipelines`, and `devops-links`
 * Feathers services (see the matching API migration + services). The DevOps
 * ViewStores read these through registered `@intrasync/stores` ServiceStores
 * (see `serviceStores/devops.stores.ts`); the row/payload shapes are kept local
 * because most of these services are live Azure proxies, not generated CRUD.
 *
 * Topology: ONE single internal Azure DevOps org. Azure DevOps *Projects* map
 * to IntraSync clients, so `devops_connections` is typically a single row whose
 * `clientId` is null (the internal org). The PAT is encrypted at rest and is
 * NEVER returned to the client — the API masks it (see {@link DevOpsConnection}
 * which carries `hasPat`, never the secret itself).
 */

export type DevOpsConnectionStatus =
  | "unverified"
  // The API's `validate` action writes "verified" on success (not "valid");
  // both are treated as the success state throughout the UI.
  | "verified"
  | "valid"
  | "invalid"
  | "error"
  | string;

export type DevOpsAuthType = "pat" | string;

/**
 * A configured Azure DevOps connection — backed by the `devops_connections`
 * table. The `encryptedPat` column is NEVER serialised to the client; the API
 * masks it and (optionally) returns a `hasPat` boolean so the UI can show
 * whether a token is on file without ever exposing it.
 */
export interface DevOpsConnection {
  id: string;
  /** null = the internal org (the typical/only row). */
  clientId?: string | null;
  name: string;
  orgUrl: string;
  authType?: DevOpsAuthType;
  /** Write-only in the UI; the API never returns the PAT itself. */
  hasPat?: boolean;
  defaultProject?: string | null;
  /** The API stores/returns scopes as a string array (or null), never a string. */
  scopes?: string[] | null;
  lastValidatedAt?: string | null;
  status?: DevOpsConnectionStatus;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: number | null;
  updatedBy?: number | null;
  deletedAt?: string | null;
}

/**
 * Editable subset of a connection used by the create/edit form. `pat` is
 * write-only — it is sent to the API on save but never read back.
 */
export interface DevOpsConnectionFormData {
  name?: string;
  clientId?: string | null;
  orgUrl?: string;
  /** Write-only — submitted to the API, never rendered back. */
  pat?: string;
  defaultProject?: string | null;
  scopes?: string | null;
}

/**
 * Raw response of the connection `validate` custom action.
 *
 * The API exposes validation via `create` (POST) with `{ action: 'validate',
 * id }` and returns the full connection record with its top-level `status`
 * ("verified" | "invalid") + `lastValidatedAt`, plus a nested `validation`
 * object carrying the project count / failure message. No secret is ever sent
 * (only the action name + id) or returned.
 */
export interface DevOpsConnectionValidateResponse {
  status: DevOpsConnectionStatus;
  lastValidatedAt?: string | null;
  validation?: {
    ok: boolean;
    status: DevOpsConnectionStatus;
    projectCount?: number;
    message?: string;
  };
}

/**
 * Normalised (flat) result of the connection `validate` action, used by the UI
 * alert. Derived from {@link DevOpsConnectionValidateResponse}.
 */
export interface DevOpsConnectionValidateResult {
  status: DevOpsConnectionStatus;
  message?: string;
  projectCount?: number;
  lastValidatedAt?: string | null;
}

/** Payload sent to trigger the `validate` action (POST, no secret). */
export interface DevOpsConnectionValidatePayload {
  action: "validate";
  id: string;
}

/** Success statuses for a connection (the API writes "verified"; UI also accepts "valid"). */
export const DEVOPS_SUCCESS_STATUSES: ReadonlyArray<string> = ["verified", "valid"];

export const isDevOpsConnectionValid = (status?: DevOpsConnectionStatus): boolean =>
  !!status && DEVOPS_SUCCESS_STATUSES.includes(status);

/** A live (read-only) Azure DevOps Project, proxied via `devops-projects`. */
export interface DevOpsProject {
  id: string;
  name: string;
  description?: string;
  url?: string;
  state?: string;
  visibility?: string;
}

/** A branch row (with ahead/behind stats) attached by `devops-repos` reads. */
export interface DevOpsBranch {
  name?: string;
  aheadCount?: number;
  behindCount?: number;
}

/** A live (read-only) Azure DevOps Git repository, proxied via `devops-repos`. */
export interface DevOpsRepo {
  id: string;
  name: string;
  projectId?: string;
  projectName?: string;
  defaultBranch?: string;
  webUrl?: string;
  size?: number;
  /** Attached only by `devops-repos` get / find-with-repoId. */
  branches?: DevOpsBranch[];
  /** Attached only when requested via `includePullRequests` (active PRs). */
  pullRequests?: DevOpsPullRequest[];
  /** UI-side enrichment — branches.length from a per-repo detail fetch. */
  branchCount?: number;
}

/** Flat string PR status the Repos board renders (API maps the Azure enum). */
export type DevOpsPullRequestStatus =
  | "active"
  | "abandoned"
  | "completed"
  | "notSet"
  | string;

/**
 * An active pull request attached to a `devops-repos` record when the read is
 * issued with `includePullRequests: true`. The API flattens the Azure
 * GitPullRequest (creator → `author` display name, deep `webUrl` built from the
 * repo URL); the status arrives as a flat string, never the numeric enum.
 */
export interface DevOpsPullRequest {
  id?: number;
  title?: string;
  status?: DevOpsPullRequestStatus;
  author?: string;
  /** e.g. "refs/heads/feature/x" — render with stripRefPrefix. */
  sourceRefName?: string;
  targetRefName?: string;
  isDraft?: boolean;
  creationDate?: string;
  webUrl?: string;
  repoId?: string;
}

/** "refs/heads/main" → "main" for compact branch display. */
export const stripRefPrefix = (ref?: string): string =>
  ref ? ref.replace(/^refs\/heads\//, "") : "";

export type DevOpsBuildStatus =
  | "succeeded"
  | "failed"
  | "partiallySucceeded"
  | "canceled"
  | "none"
  | "inProgress"
  | "notStarted"
  | string;

/**
 * A live (read-only) Azure DevOps build/pipeline definition with its latest
 * build status, proxied via `devops-pipelines`.
 */
export interface DevOpsPipeline {
  id: string;
  name: string;
  projectId?: string;
  projectName?: string;
  path?: string;
  webUrl?: string;
  /** Latest build outcome, surfaced by the API for the status board. */
  latestStatus?: DevOpsBuildStatus;
  /** Build pipeline run state (queue/in-progress/completed). */
  latestState?: string;
  /** Azure DevOps build id of the latest run — the cancel target. */
  latestBuildId?: string;
  latestBuildNumber?: string;
  latestFinishTime?: string | null;
  latestSourceBranch?: string;
}

/**
 * Raw `devops-pipelines` record as the API actually serialises it: the build
 * definition fields plus a NESTED `latestBuild` object whose `status`/`result`
 * are numeric Azure DevOps enum codes. The UI consumes the flat
 * {@link DevOpsPipeline} instead — see {@link normalizePipeline}.
 */
export interface DevOpsPipelineApiRecord {
  id?: number;
  name?: string;
  path?: string;
  revision?: number;
  /** Azure returns `url`, not `webUrl`. */
  url?: string;
  queueStatus?: string;
  projectId?: string;
  projectName?: string;
  latestBuild?: {
    id?: number;
    buildNumber?: string;
    /** Azure BuildStatus enum: 1=InProgress, 2=Completed, 4=Cancelling, 8=Postponed, 32=NotStarted. */
    status?: number;
    /** Azure BuildResult enum: 2=Succeeded, 4=PartiallySucceeded, 8=Failed, 32=Canceled. */
    result?: number;
    startTime?: string;
    finishTime?: string;
    sourceBranch?: string;
  } | null;
}

// Azure DevOps BuildStatus / BuildResult enum codes (the wire values).
const AZ_BUILD_STATUS_COMPLETED = 2;
const AZ_BUILD_STATUS_IN_PROGRESS = 1;
const AZ_BUILD_STATUS_NOT_STARTED = 32;
const AZ_BUILD_RESULT: Record<number, DevOpsBuildStatus> = {
  2: "succeeded",
  4: "partiallySucceeded",
  8: "failed",
  32: "canceled",
};

/**
 * Translate the API's nested `latestBuild` (numeric Azure enums) into the flat,
 * string-valued {@link DevOpsPipeline} the board renders. Without this the board
 * shows every pipeline as "Unknown" with empty build/branch/finished columns
 * and the Succeeded/Failed/Running summary counts stay at 0.
 */
export function normalizePipeline(rec: DevOpsPipelineApiRecord): DevOpsPipeline {
  const b = rec.latestBuild;
  let latestStatus: DevOpsBuildStatus = "none";
  let latestState: string | undefined;
  if (b) {
    if (b.status === AZ_BUILD_STATUS_COMPLETED) {
      latestStatus =
        (b.result != null && AZ_BUILD_RESULT[b.result]) || "none";
      latestState = "completed";
    } else if (b.status === AZ_BUILD_STATUS_IN_PROGRESS) {
      latestStatus = "inProgress";
      latestState = "inProgress";
    } else if (b.status === AZ_BUILD_STATUS_NOT_STARTED) {
      latestStatus = "notStarted";
      latestState = "notStarted";
    } else if (b.result != null && AZ_BUILD_RESULT[b.result]) {
      // Status unknown but a result is present — fall back to the result.
      latestStatus = AZ_BUILD_RESULT[b.result];
    }
  }
  return {
    id: String(rec.id ?? ""),
    name: rec.name ?? "",
    projectId: rec.projectId,
    projectName: rec.projectName,
    path: rec.path,
    webUrl: rec.url,
    latestStatus,
    latestState,
    latestBuildId: b?.id != null ? String(b.id) : undefined,
    latestBuildNumber: b?.buildNumber,
    latestFinishTime: b?.finishTime ?? null,
    latestSourceBranch: b?.sourceBranch,
  };
}

// ---------------------------------------------------------------------------
// Phase 3 — pipeline write actions (queue / cancel a build)
// ---------------------------------------------------------------------------

/**
 * Payload sent to `devops-pipelines` (POST/create) to QUEUE a new build for a
 * build definition. The `devops-pipelines` service is a live Azure DevOps proxy
 * with NO local row, so the write goes through `create` carrying an `{ action }`
 * discriminator (mirroring the `devops-connections` `validate` action). No
 * secret is ever sent — only the action + identifiers; the API resolves the
 * connection's encrypted PAT server-side.
 */
export interface DevOpsQueueBuildPayload {
  action: "queueBuild";
  connectionId: string;
  /**
   * The Azure DevOps project the definition lives in. The API reads this as
   * `data.project` (NOT `projectId`) and passes it straight to the Build API,
   * which accepts either the project GUID or its name — we send the GUID
   * (`selectedProjectId`).
   */
  project: string;
  /** Build definition id to queue — the API requires a NUMBER. */
  definitionId: number;
  /** Optional branch to build (e.g. "refs/heads/main"). */
  sourceBranch?: string;
}

/**
 * Payload sent to `devops-pipelines` (POST/create) to CANCEL an in-progress
 * build. Operator/admin only (enforced by the API RBAC seam). No secret sent.
 */
export interface DevOpsCancelBuildPayload {
  action: "cancelBuild";
  connectionId: string;
  /** Azure DevOps project — read as `data.project` by the API (see above). */
  project: string;
  /** The running build id to cancel — the API requires a NUMBER. */
  buildId: number;
}

/**
 * Response of a queue/cancel action — the API echoes the affected build with
 * the same nested numeric-enum `latestBuild` shape used by reads. The store
 * normalises it via {@link normalizePipeline} where it needs the flat form.
 */
export interface DevOpsBuildActionResponse {
  /** The queued/cancelled build id (Azure DevOps build id). */
  buildId?: number;
  /** The owning definition id, so the board can map it back to a row. */
  definitionId?: number;
  buildNumber?: string;
  /** Azure BuildStatus enum (1=InProgress, 2=Completed, 32=NotStarted). */
  status?: number;
  /** Azure BuildResult enum (2=Succeeded, 8=Failed, 32=Canceled). */
  result?: number;
  sourceBranch?: string;
  finishTime?: string | null;
}

// ---------------------------------------------------------------------------
// Phase 3 — live status event (Socket.io `devops-event`)
// ---------------------------------------------------------------------------

/** What kind of entity a live `devops-event` concerns. */
export type DevOpsEventEntity = "build" | "pipeline" | "workItem" | string;

/**
 * Realtime envelope emitted by the API on the `devops-event` Socket.io channel
 * (mirrors the API's webhook → channel publish). The board/work-items stores
 * patch the matching row in place rather than reloading. All fields optional —
 * consumers must tolerate partial payloads.
 */
export interface DevOpsEvent {
  /** Which kind of entity changed. */
  entity: DevOpsEventEntity;
  /** Connection/project the event belongs to (for scoping to the open view). */
  connectionId?: string;
  projectId?: string;
  /** For build/pipeline events: the build definition (pipeline row) id. */
  definitionId?: string | number;
  /** For build events: the specific build id. */
  buildId?: string | number;
  /** For workItem events: the work item id. */
  workItemId?: string | number;
  /** Azure BuildStatus enum (numeric) when the API forwards it raw. */
  status?: number | string;
  /** Azure BuildResult enum (numeric) when present. */
  result?: number | string;
  buildNumber?: string;
  sourceBranch?: string;
  finishTime?: string | null;
  /** For workItem events: the new state (e.g. "Active", "Closed"). */
  state?: string;
  /** Free-form fields the API forwards (e.g. changed work-item fields). */
  fields?: Record<string, unknown>;
  /** When the event occurred (ISO). */
  at?: string;
}

// ---------------------------------------------------------------------------
// Phase 3 — Work Items (WIQL query + state/field updates)
// ---------------------------------------------------------------------------

/**
 * A live (read-only-ish) Azure DevOps Work Item, proxied via
 * `devops-work-items`. Reads run a WIQL query; writes patch `state`/fields.
 * The flat shape the table renders — the API flattens the Azure `fields`
 * dictionary (System.Title, System.State, etc.) into these named props.
 */
export interface DevOpsWorkItem {
  id: string;
  /** System.WorkItemType — Bug, Task, User Story, etc. */
  workItemType?: string;
  /** System.Title */
  title?: string;
  /** System.State — New, Active, Resolved, Closed, … */
  state?: string;
  /** System.AssignedTo display name. */
  assignedTo?: string | null;
  /** System.Tags (semicolon-separated in Azure; surfaced as a string). */
  tags?: string | null;
  /** Microsoft.VSTS.Common.Priority */
  priority?: number | null;
  /** System.AreaPath / System.IterationPath */
  areaPath?: string | null;
  iterationPath?: string | null;
  url?: string;
  changedDate?: string | null;
  createdDate?: string | null;
}

/**
 * Raw `devops-work-items` record as the API may serialise it: the Azure
 * `fields` dictionary keyed by reference name. Consumers normalise it to the
 * flat {@link DevOpsWorkItem} via {@link normalizeWorkItem}.
 */
export interface DevOpsWorkItemApiRecord {
  id?: number | string;
  url?: string;
  fields?: Record<string, unknown>;
  /** Some API shapes pre-flatten; tolerate both. */
  [key: string]: unknown;
}

/**
 * Payload to run a WIQL query via `devops-work-items` find. WIQL is passed as a
 * query param; the API runs it through the connection's PAT and returns the
 * referenced work items (hydrated). A saved-query id may be used instead.
 */
export interface DevOpsWiqlQueryParams {
  connectionId: string;
  projectId: string;
  /** Raw WIQL text — e.g. SELECT [System.Id] FROM WorkItems WHERE … */
  wiql?: string;
  /** Alternatively, a stored query id/path to execute. */
  queryId?: string;
}

/** Editable subset used by the work-item update form. */
export interface DevOpsWorkItemFormData {
  state?: string;
  title?: string;
  assignedTo?: string | null;
  priority?: number | null;
  tags?: string | null;
}

/** A single Azure DevOps JSON-Patch operation (the wire shape the API applies). */
export interface DevOpsJsonPatchOp {
  op: "add" | "replace" | "remove" | "test";
  path: string;
  value?: unknown;
}

/**
 * Payload sent to `devops-work-items` PATCH to update a work item.
 *
 * The API does NOT accept named fields — it requires a raw Azure JSON-Patch
 * document under `ops` (e.g. `{ op: 'add', path: '/fields/System.State', value }`)
 * plus `connectionId` and `project` (NOT `projectId`). See
 * {@link buildWorkItemPatchOps} which assembles `ops` from the form fields.
 */
export interface DevOpsWorkItemUpdatePayload {
  connectionId: string;
  /** Azure DevOps project — the API reads `data.project`, not `projectId`. */
  project: string;
  /** Azure JSON-Patch document the API applies via updateWorkItem. */
  ops: DevOpsJsonPatchOp[];
}

/** Azure field reference names the update form maps onto JSON-Patch paths. */
const WI_UPDATE_FIELDS = {
  state: "System.State",
  title: "System.Title",
  assignedTo: "System.AssignedTo",
  priority: "Microsoft.VSTS.Common.Priority",
  tags: "System.Tags",
} as const;

/**
 * Build the Azure JSON-Patch `ops` document the API's `devops-work-items` patch
 * requires, from the flat form data. Only fields the caller actually set are
 * emitted (blank optional fields are left unchanged). `state` is required by the
 * form validation, so it is always present.
 */
export function buildWorkItemPatchOps(
  form: DevOpsWorkItemFormData
): DevOpsJsonPatchOp[] {
  const ops: DevOpsJsonPatchOp[] = [];
  const push = (field: string, value: unknown) =>
    ops.push({ op: "add", path: `/fields/${field}`, value });

  if (form.state && form.state.trim()) {
    push(WI_UPDATE_FIELDS.state, form.state.trim());
  }
  if (form.title && form.title.trim()) {
    push(WI_UPDATE_FIELDS.title, form.title.trim());
  }
  if (form.assignedTo != null) {
    const v = String(form.assignedTo).trim();
    // Empty string clears the assignment; Azure accepts an empty value here.
    push(WI_UPDATE_FIELDS.assignedTo, v);
  }
  if (form.priority != null) {
    push(WI_UPDATE_FIELDS.priority, form.priority);
  }
  if (form.tags != null) {
    push(WI_UPDATE_FIELDS.tags, String(form.tags).trim());
  }
  return ops;
}

/** Azure work-item `fields` reference names we read/flatten. */
const WI_FIELDS = {
  type: "System.WorkItemType",
  title: "System.Title",
  state: "System.State",
  assignedTo: "System.AssignedTo",
  tags: "System.Tags",
  priority: "Microsoft.VSTS.Common.Priority",
  areaPath: "System.AreaPath",
  iterationPath: "System.IterationPath",
  changedDate: "System.ChangedDate",
  createdDate: "System.CreatedDate",
} as const;

/** Azure `AssignedTo` is sometimes an object ({ displayName }) — coerce to a name. */
function coerceIdentity(v: unknown): string | null {
  if (v == null) return null;
  if (typeof v === "string") return v;
  if (typeof v === "object") {
    const o = v as { displayName?: unknown; uniqueName?: unknown };
    return (
      (typeof o.displayName === "string" && o.displayName) ||
      (typeof o.uniqueName === "string" && o.uniqueName) ||
      null
    );
  }
  return String(v);
}

/**
 * Translate the API's raw work-item record (Azure `fields` dictionary) into the
 * flat {@link DevOpsWorkItem} the table renders. Tolerates an already-flat
 * record (returns its props directly when no `fields` dictionary is present).
 */
export function normalizeWorkItem(rec: DevOpsWorkItemApiRecord): DevOpsWorkItem {
  const f = rec.fields;
  if (!f) {
    // Already flat — pass through the recognised props.
    return {
      id: String(rec.id ?? (rec as { id?: unknown }).id ?? ""),
      workItemType: (rec as { workItemType?: string }).workItemType,
      title: (rec as { title?: string }).title,
      state: (rec as { state?: string }).state,
      assignedTo: coerceIdentity((rec as { assignedTo?: unknown }).assignedTo),
      tags: (rec as { tags?: string | null }).tags ?? null,
      priority: (rec as { priority?: number | null }).priority ?? null,
      areaPath: (rec as { areaPath?: string | null }).areaPath ?? null,
      iterationPath: (rec as { iterationPath?: string | null }).iterationPath ?? null,
      url: rec.url,
      changedDate: (rec as { changedDate?: string | null }).changedDate ?? null,
      createdDate: (rec as { createdDate?: string | null }).createdDate ?? null,
    };
  }
  const num = (v: unknown): number | null =>
    typeof v === "number" ? v : v != null && !Number.isNaN(Number(v)) ? Number(v) : null;
  const str = (v: unknown): string | undefined =>
    v == null ? undefined : String(v);
  return {
    id: String(rec.id ?? ""),
    workItemType: str(f[WI_FIELDS.type]),
    title: str(f[WI_FIELDS.title]),
    state: str(f[WI_FIELDS.state]),
    assignedTo: coerceIdentity(f[WI_FIELDS.assignedTo]),
    tags: str(f[WI_FIELDS.tags]) ?? null,
    priority: num(f[WI_FIELDS.priority]),
    areaPath: str(f[WI_FIELDS.areaPath]) ?? null,
    iterationPath: str(f[WI_FIELDS.iterationPath]) ?? null,
    url: rec.url,
    changedDate: str(f[WI_FIELDS.changedDate]) ?? null,
    createdDate: str(f[WI_FIELDS.createdDate]) ?? null,
  };
}

/** Default WIQL — open (not Closed/Removed) items in the project, newest first. */
export const DEFAULT_WIQL =
  "SELECT [System.Id], [System.Title], [System.State], [System.WorkItemType], [System.AssignedTo] " +
  "FROM WorkItems " +
  "WHERE [System.State] <> 'Closed' AND [System.State] <> 'Removed' " +
  "ORDER BY [System.ChangedDate] DESC";

/** Common work-item state options offered by the update form (free-text fallback allowed). */
export const COMMON_WORK_ITEM_STATES: ReadonlyArray<string> = [
  "New",
  "Active",
  "Resolved",
  "Closed",
  "Removed",
  "To Do",
  "Doing",
  "Done",
];

// ---------------------------------------------------------------------------
// Service-hook subscriptions (`devops-subscriptions`)
// ---------------------------------------------------------------------------

export type DevOpsSubscriptionStatus =
  | "pending"
  | "active"
  | "failed"
  | "disabled"
  | string;

/**
 * A registered Azure DevOps service-hook subscription — backed by the
 * `devops_subscriptions` table. The per-subscription shared secret is NEVER
 * returned (only `hasSecret`); `consumerUrl` is our public receiver endpoint.
 *
 * SCOPE: since migration 20260903000003 the API records the scope a create
 * carried as three FLAT columns (`devopsProjectId` / `devopsProjectName` /
 * `devopsEntityId`) and returns them on every row. It does not return the
 * raw `publisherInputs` it forwarded to Azure; that field is kept optional
 * only so an older API (or a hand-built fixture) still renders. Readers take
 * `devopsProjectId ?? publisherInputs?.projectId` for the id and prefer
 * `devopsProjectName` for display. A row with all three NULL (every one from
 * before that migration) is of UNKNOWN scope — not verified org-wide.
 */
export interface DevOpsSubscription {
  id: string;
  connectionId: string;
  /** e.g. 'build.complete', 'workitem.updated', 'git.pullrequest.created'. */
  eventType: string;
  /** Azure-assigned subscription id (null while pending/failed). */
  remoteSubscriptionId?: string | null;
  /** Our inbound webhook receiver URL Azure posts to. */
  consumerUrl: string;
  /** True when a shared secret is stored. The secret itself is NEVER returned. */
  hasSecret?: boolean;
  status: DevOpsSubscriptionStatus;
  /** Azure project GUID the hook is scoped to; null = scope not recorded. */
  devopsProjectId?: string | null;
  /** That project's display name, when the creator sent one. */
  devopsProjectName?: string | null;
  /** Narrower scope inside the project (a build definition / repository id). */
  devopsEntityId?: string | null;
  /** Scope inputs sent to Azure (projectId etc.) — only an older API returns them. */
  publisherInputs?: Record<string, string>;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: number | null;
  deletedAt?: string | null;
}

/**
 * Payload sent to `devops-subscriptions` create. Project scoping goes through
 * `publisherInputs.projectId` (the API passes `publisherInputs` straight to
 * Azure's subscription create) — NOT the dead top-level `project` field.
 */
export interface DevOpsSubscriptionCreatePayload {
  connectionId: string;
  eventType: string;
  publisherInputs?: Record<string, string>;
  /**
   * The scoped project's display name. The API stores it as the row's
   * `devopsProjectName` (never sent to Azure); without it the row carries a
   * GUID and no name, and the overview then lists the project as `name: null`.
   * Sent whenever the caller knows the name.
   */
  projectName?: string;
}

/** Editable subset used by the Add Subscription dialog. */
export interface DevOpsSubscriptionFormData {
  eventType?: string;
  /** Azure DevOps project GUID the hook is scoped to (→ publisherInputs.projectId). */
  projectId?: string | null;
}

/**
 * Event types offered by the Add Subscription dialog. The API accepts any
 * Azure service-hook event type string; these are the ones the inbound
 * receiver actually parses (build.complete / workitem.* / git.pullrequest.*)
 * plus the release deployment event (publisher 'rm', mapped by the API's
 * publisherIdForEvent).
 */
export const DEVOPS_SUBSCRIPTION_EVENT_TYPES: ReadonlyArray<{
  value: string;
  label: string;
}> = [
  { value: "build.complete", label: "Build completed" },
  { value: "git.pullrequest.created", label: "Pull request created" },
  { value: "git.pullrequest.updated", label: "Pull request updated" },
  { value: "workitem.created", label: "Work item created" },
  { value: "workitem.updated", label: "Work item updated" },
  {
    value: "ms.vss-release.deployment-completed-event",
    label: "Release deployment completed",
  },
];

// ---------------------------------------------------------------------------
// Releases (`devops-releases` — live Azure DevOps Release proxy)
// ---------------------------------------------------------------------------

/** Flat string release status the board renders. */
export type DevOpsReleaseStatus =
  | "draft"
  | "active"
  | "abandoned"
  | "undefined"
  | string;

/**
 * Raw `devops-releases` record as the API serialises it — `status` is the
 * NUMERIC Azure ReleaseStatus enum (0=Undefined, 1=Draft, 2=Active,
 * 4=Abandoned). The UI consumes the flat {@link DevOpsRelease} instead — see
 * {@link normalizeRelease} (mirrors `normalizePipeline` for builds).
 */
export interface DevOpsReleaseApiRecord {
  id?: number;
  name?: string;
  /** Azure ReleaseStatus enum: 0=Undefined, 1=Draft, 2=Active, 4=Abandoned. */
  status?: number;
  createdOn?: string;
  modifiedOn?: string;
  definitionId?: number;
  definitionName?: string;
  description?: string;
  webUrl?: string;
}

/** Flat release row the Releases board renders. */
export interface DevOpsRelease {
  id: string;
  name: string;
  status: DevOpsReleaseStatus;
  createdOn?: string | null;
  modifiedOn?: string | null;
  definitionId?: string;
  definitionName?: string;
  description?: string;
  webUrl?: string;
}

// Azure ReleaseStatus enum codes (the wire values).
const AZ_RELEASE_STATUS: Record<number, DevOpsReleaseStatus> = {
  0: "undefined",
  1: "draft",
  2: "active",
  4: "abandoned",
};

/**
 * Translate the API's numeric-enum release record into the flat, string-valued
 * {@link DevOpsRelease} the board renders (mirrors {@link normalizePipeline}).
 */
export function normalizeRelease(rec: DevOpsReleaseApiRecord): DevOpsRelease {
  return {
    id: String(rec.id ?? ""),
    name: rec.name ?? "",
    status:
      (rec.status != null && AZ_RELEASE_STATUS[rec.status]) || "undefined",
    createdOn: rec.createdOn ?? null,
    modifiedOn: rec.modifiedOn ?? null,
    definitionId: rec.definitionId != null ? String(rec.definitionId) : undefined,
    definitionName: rec.definitionName,
    description: rec.description,
    webUrl: rec.webUrl,
  };
}

/**
 * Payload sent to `devops-releases` create to START a release for a release
 * definition. Operator/admin only (API `requireActionAccess`). The API reads
 * `data.project` (the Azure project GUID or name) and requires a NUMERIC
 * `definitionId`. No secret is ever sent.
 */
export interface DevOpsReleaseCreatePayload {
  connectionId: string;
  /** Azure DevOps project — the API reads `data.project`, not `projectId`. */
  project: string;
  /** Release definition id to start — the API requires a NUMBER. */
  definitionId: number;
  description?: string;
}

/** Editable subset used by the Create Release dialog. */
export interface DevOpsReleaseFormData {
  /** Kept as text in the form; validated/coerced to a number on save. */
  definitionId?: string;
  description?: string;
}

export type DevOpsLinkType = "project" | "repo" | "pipeline" | string;

/**
 * A persisted association between an IntraSync client/project and an Azure
 * DevOps entity — backed by the `devops_links` table.
 */
export interface DevOpsLink {
  id: string;
  linkType: DevOpsLinkType;
  clientId?: string | null;
  intrasyncProjectId?: number | null;
  connectionId: string;
  devopsProjectId: string;
  devopsProjectName?: string | null;
  devopsEntityId?: string | null;
  devopsEntityName?: string | null;
  /**
   * The governed module (module_catalog.moduleKey) this link is attributed to,
   * or null when none was chosen. Set from the Client 360 wizard/edit dialog,
   * the Overview's "Link a project" and Project 360; read by Module 360 and the
   * overview's module chips. Patchable on its own (`devops-links.patch`).
   */
  governedSlug: string | null;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: number | null;
  deletedAt?: string | null;
}

/** Editable subset used by the create-link flow. */
export interface DevOpsLinkFormData {
  linkType?: DevOpsLinkType;
  clientId?: string | null;
  intrasyncProjectId?: number | null;
  connectionId?: string;
  devopsProjectId?: string;
  devopsProjectName?: string | null;
  devopsEntityId?: string | null;
  devopsEntityName?: string | null;
  /**
   * Connect-wizard: event types to subscribe to (devops-subscriptions create)
   * AFTER the link saves, scoped via publisherInputs to the linked project.
   * Per-subscription failures never roll the link back.
   */
  subscribeEvents?: string[];
}

/**
 * Event types offered by the Client 360 "Add Link" connect wizard — the subset
 * of {@link DEVOPS_SUBSCRIPTION_EVENT_TYPES} that powers the per-client live
 * surfaces (builds board, PR visibility, work-item updates).
 */
export const DEVOPS_LINK_WIZARD_EVENT_TYPES: ReadonlyArray<{
  value: string;
  label: string;
}> = [
  { value: "build.complete", label: "Build completed" },
  { value: "git.pullrequest.created", label: "Pull request created" },
  { value: "workitem.updated", label: "Work item updated" },
];

// ── DevOps overview (2026-09-03) — the client×project rollup ───────────────
//
// The response of the find-only `devops-overview` service: ONE call, local
// data (links, events, subscriptions, module catalog, clients), optionally
// decorated with a cached live project list. The landing page and Project 360
// read it. Shapes mirror git show 23f8d85^:plan/2026-09-03_devops-by-client-and-project.md §2.1
// exactly — change them there first.

/** Where HQ first learned of a project. `catalog` is typed but the API never
 *  produces it (a catalog row names a project by name only, so an unmatched one
 *  lands in `mismatches`); `subscription` is a project-scoped webhook row with
 *  no link, event or live row behind it. */
export type DevOpsProjectSource = "link" | "catalog" | "live" | "events" | "subscription";

export type DevOpsAttention =
  | "failed-build"
  | "no-links"
  | "no-subscriptions"
  | "module-mapping-mismatch"
  | "no-activity"
  /** CLIENTS only: a project this client links had activity its own links
   *  cannot claim (a shared project, where an event matching no entity link
   *  belongs to nobody in particular). The client's counts are a FLOOR, and
   *  `no-activity` is suppressed while this is set — the card must never call
   *  a busy project quiet. */
  | "unattributed-activity";

export interface DevOpsOverviewClientRef {
  id: string;
  name: string;
  code: string | null;
}

export interface DevOpsOverviewModuleRef {
  governedSlug: string;
  displayName: string;
  /** True when approval routing (module_catalog.adoProject) names THIS project. */
  routesHere: boolean;
}

export interface DevOpsOverviewLink {
  id: string;
  linkType: DevOpsLinkType;
  clientId: string | null;
  governedSlug: string | null;
  entityId: string | null;
  entityName: string | null;
}

export interface DevOpsOverviewSubscriptions {
  active: number;
  pending: number;
  failed: number;
  eventTypes: string[];
  /** Active rows with no project scope (every pre-2026-09-03 row) — they may
   *  or may not cover this project, so they suppress `no-subscriptions` but
   *  are never attributed. The API always sends it (0 when none); optional
   *  here only so hand-built fixtures need not carry it. */
  connectionWide?: number;
}

export interface DevOpsOverviewLastBuild {
  result: string | null;
  buildNumber: string | null;
  definitionName: string | null;
  finishedAt: string | null;
}

export interface DevOpsOverviewActivity {
  lastAt: string | null;
  builds: number;
  failedBuilds: number;
  pullRequests: number;
  workItems: number;
  releases: number;
  lastBuild: DevOpsOverviewLastBuild | null;
}

export interface DevOpsOverviewProject {
  /** `${connectionId}:${projectId}` — the card key and the Project 360 route
   *  key; `${connectionId}:${name}` for a project HQ only ever saw by name. */
  key: string;
  connectionId: string;
  /** Azure's project GUID, or null for a project known by name only (name-only
   *  events with no link). Azure accepts either as a project reference. */
  projectId: string | null;
  /** The project's name, or null when HQ has only an id for it — a
   *  subscription row scoped by GUID with no name recorded (every one the web
   *  created before `projectName` rode on the create payload). Render through
   *  `projectLabel()` (devopsOverview.helpers), never `.name` directly. */
  name: string | null;
  source: DevOpsProjectSource;
  clients: DevOpsOverviewClientRef[];
  modules: DevOpsOverviewModuleRef[];
  links: DevOpsOverviewLink[];
  subscriptions: DevOpsOverviewSubscriptions;
  activity: DevOpsOverviewActivity;
  attention: DevOpsAttention[];
}

export interface DevOpsOverviewClient {
  id: string;
  name: string;
  code: string | null;
  projectKeys: string[];
  activity: DevOpsOverviewActivity;
  attention: DevOpsAttention[];
}

export interface DevOpsOverviewConnection {
  id: string;
  name: string;
  status: string | null;
  isInternal: boolean;
}

export interface DevOpsOverviewMismatches {
  modulesRoutingToUnknownProject: Array<{ governedSlug: string; displayName: string; adoProject: string }>;
  projectsWithoutLinks: string[];
  linkedProjectsNotRoutedByAnyModule: string[];
}

export interface DevOpsOverview {
  generatedAt: string;
  windowDays: number;
  connections: DevOpsOverviewConnection[];
  projects: DevOpsOverviewProject[];
  clients: DevOpsOverviewClient[];
  mismatches: DevOpsOverviewMismatches;
  /** Always present; `{ fetched: false, note: null }` when includeLive was not asked. */
  /**
   * The optional live read of each connection's project list.
   * `fetched` = Azure answered for every connection asked; `partial` = the list
   * is KNOWN to be incomplete (the page cap was hit with a continuation token
   * outstanding), so a project missing from it has NOT been shown to be gone;
   * `note` = the server's own sentence about either, rendered verbatim.
   */
  live: { fetched: boolean; partial?: boolean; note: string | null };
}

/** The query the overview accepts. */
export interface DevOpsOverviewQuery {
  clientId?: string;
  connectionId?: string;
  windowDays?: number;
  includeLive?: boolean;
}
